/** Форматы и подписи «WB: регионы» (не компоненты): null — «нет данных» / «нет спроса», 0 —
 *  «0»; класс дефицита строки витрины — подпись и цвет; даты «сейчас» по кабинетам. */
import type { DeficitClass, LegalDates } from "@/api/wbRegions";
import { fmtWhen, int, NBSP } from "../ozon-analytics/format";

export const NO_DATA = "нет данных";
export const NO_DEMAND = "нет спроса";
/** Уверенность FBO-оценки ниже — «слабая»: серым курсивом, пометка в ячейке матрицы. */
export const WEAK_CONFIDENCE = 0.8;
/** Пометки неполного дня строки витрины (правило 7, контракт v1.3: `sales_complete = false` —
 *  логистика реализации ещё не дошла до дня, продажи неполные — не ноль). */
export const ORDERS_INCOMPLETE = "заказы дня неполные";
export const SALES_INCOMPLETE = "продажи неполные (логистика реализации отстаёт)";

/** Среднее в день / дни: 1 знак после запятой. */
export function dec1(v: number): string {
  const [i, f] = Math.abs(v).toFixed(1).split(".");
  return (v < 0 && Number(i) + Number(f) > 0 ? "−" : "") + int(Number(i)) + "," + f;
}

export function daysText(v: number | null, demand: number | null): string {
  if (v !== null) return `${dec1(v)}${NBSP}дн.`;
  return demand === 0 ? NO_DEMAND : NO_DATA;
}

export function qtyText(v: number | null): string {
  return v === null ? NO_DATA : int(v);
}

/** Уверенность слабая (< 0,8): FBO-оценка — пометкой, не как факт. null — доли нет (FBO 0). */
export function isWeakConfidence(v: number | null): boolean {
  return v !== null && v < WEAK_CONFIDENCE;
}

export function confidenceTitle(p: {
  fbo_stock_known_chain: number | null;
  fbo_stock_by_supply_signal: number | null;
  fbo_stock_weak: number | null;
  fbo_stock_unresolved: number | null;
}): string {
  return [
    `склад виден / по цепочке: ${qtyText(p.fbo_stock_known_chain)}`,
    `по сигналам поставки: ${qtyText(p.fbo_stock_by_supply_signal)}`,
    `слабо (attr19, боковые строки): ${qtyText(p.fbo_stock_weak)}`,
    `склад не определён: ${qtyText(p.fbo_stock_unresolved)}`,
  ].join("\n");
}

/** Цвет и подпись класса дефицита строки витрины (dwh `deficit_class`). ok — без цвета. */
export const DEFICIT_STYLE: Record<DeficitClass, { label: string; cls: string }> = {
  zero: {
    label: "дефицит",
    cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  },
  low: {
    label: "мало",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
  ok: { label: "норма", cls: "" },
  excess: {
    label: "избыток",
    cls: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300",
  },
};

export function deficitLabel(c: DeficitClass | null): string {
  return c === null ? NO_DEMAND : DEFICIT_STYLE[c].label;
}

/** Класс ячейки по `deficit_class`: null («нет спроса») — серым. */
export function deficitClassName(c: DeficitClass | null): string {
  return c === null ? "text-muted-foreground" : DEFICIT_STYLE[c].cls;
}

/** Дата «сейчас» по кабинетам: одна у всех — одной датой, разные — «кабинет дата» через «·». */
export function datesText(dates: Record<string, LegalDates>, key: keyof LegalDates): string {
  const entries = Object.entries(dates);
  const uniq = [...new Set(entries.map(([, d]) => d[key]))];
  if (uniq.length === 1) return fmtWhen(uniq[0] ?? null);
  return entries.map(([l, d]) => `${l} ${fmtWhen(d[key])}`).join(" · ");
}
